import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'

export interface OrderMessage {
  id: string
  senderType: 'customer' | 'owner'
  senderId: string
  senderName: string
  body: string
  createdAt: string
}

/** チャットの取得間隔。開いている間だけ回る。 */
const POLL_INTERVAL_MS = 3000

interface Options {
  /** どちらの API を叩くか。客は自分の注文、店は owner/staff として読む。 */
  role: 'customer' | 'owner'
  /** 客側のみ。ログイン客の userId か、ゲストなら undefined (ゲストIDは apiFetch が載せる)。 */
  userId?: string | null
}

function mapMessage(m: Record<string, unknown>): OrderMessage {
  return {
    id: String(m.id ?? ''),
    senderType: m.senderType as OrderMessage['senderType'],
    senderId: String(m.senderId ?? ''),
    senderName: String(m.senderName ?? ''),
    body: String(m.body ?? ''),
    // API は Firestore の timestamp を ISO 文字列で返す。
    createdAt: typeof m.createdAt === 'string' ? m.createdAt : '',
  }
}

/**
 * #338 注文チャットの取得。**#1025 で onSnapshot 直読からポーリングへ変更した。**
 *
 * `orders/{id}/messages` の firestore.rules が `allow read: if true` だったため、
 * 未認証クライアントから**全店のチャット本文 (senderName + body) が読めた**。
 * rules を deny にして、読み出しを当事者チェック付きの API に寄せている。
 *
 * リアルタイム性はポーリングで代替する。チャットは注文 1 件あたり数通の低頻度な
 * やりとりなので、3 秒間隔で体感差はほぼ無い (#1024 の予約が 15 秒ポーリングで
 * 足りたのと同じ判断)。**シートが開いている間だけ回す** — orderId に null を
 * 渡せば止まる。
 *
 * 自分が送った直後だけは待たせたくないので `refetch` を返す。送信ハンドラから
 * 呼べば、次の tick を待たずに反映される。
 */
export function useOrderMessages(orderId: string | undefined | null, opts: Options) {
  const { role, userId } = opts
  const [messages, setMessages] = useState<OrderMessage[]>([])
  // 取得済みの orderId。結果が「今開いているスレッド」のものかを判定する。
  // loading はこれと orderId から derive する (render 中の setState を避ける)。
  const [loadedFor, setLoadedFor] = useState<string | null>(null)

  // 最新値を effect の依存に持ち込まずに使う (依存に入れると毎 render で
  // interval が張り直される)。
  const roleRef = useRef(role)
  roleRef.current = role
  const userIdRef = useRef(userId)
  userIdRef.current = userId

  const fetchMessages = useCallback(async (id: string) => {
    try {
      const res = roleRef.current === 'owner'
        ? await api.getOwnerOrderMessages(id)
        : await api.getOrderMessages(id, userIdRef.current || undefined)
      const list: unknown = res?.messages
      if (!Array.isArray(list)) {
        // 403 / 404 は {detail} が返る。**空配列で塗り潰さない** — 一過性の
        // 失敗で既読済みの履歴が消えると、会話が無かったように見える。
        console.error('[useOrderMessages] unexpected response:', res)
        return
      }
      setMessages(list.map((m) => mapMessage(m as Record<string, unknown>)))
    } catch (e) {
      console.error('[useOrderMessages] fetch failed:', e)
    } finally {
      setLoadedFor(id)
    }
  }, [])

  useEffect(() => {
    if (!orderId) {
      setMessages([])
      setLoadedFor(null)
      return
    }
    setMessages([])
    setLoadedFor(null)
    let cancelled = false
    const tick = () => { if (!cancelled) void fetchMessages(orderId) }
    tick()
    const timer = window.setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [orderId, fetchMessages])

  // loading は「開いていて、まだ最初の応答が来ていない」間だけ true。
  const matched = !!orderId && loadedFor === orderId

  const refetch = useCallback(() => {
    if (orderId) void fetchMessages(orderId)
  }, [orderId, fetchMessages])

  return { messages: matched ? messages : [], loading: !!orderId && !matched, refetch }
}
